import React from "react";
import { Check, Waves, Brain, Layers, Atom } from "lucide-react";
import { setupPoints, shift, RESERVE_URL } from "../mock";

const SectionTitle = ({ kicker, children }) => (
  <>
    {kicker && <span className="kicker">{kicker}</span>}
    <h2 className="font-display text-3xl md:text-5xl font-semibold text-white mt-3 leading-tight">
      {children}
    </h2>
  </>
);

export default function IntroShift() {
  return (
    <section id="shift" className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5">
        {/* Intro setup */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <SectionTitle kicker="Si tu es thérapeute / accompagnant">
              Les techniques ne suffisent pas.
              <br />
              Le vrai levier, c'est ton{" "}
              <span className="aqua-gradient-text">setup</span>.
            </SectionTitle>
            <p className="mt-5 text-white/70 leading-relaxed">
              Ici, on ne vient pas « apprendre un truc de plus ».
              On vient entrer dans une autre ligue : la pratique high level.
            </p>
            <ul className="mt-7 space-y-3">
              {setupPoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-1 grid place-items-center h-5 w-5 rounded-full bg-[#e8c874]/15 text-gold shrink-0">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-white/85">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="reveal grid sm:grid-cols-2 gap-4">
            {[
              { icon: Waves, t: "État", d: "ton eau intérieure cohérente change le champ." },
              { icon: Layers, t: "Contenance", d: "clients plus en sécurité, toi plus stable." },
              { icon: Brain, t: "Guidance pure", d: "sans filtres, sans projections." },
              { icon: Atom, t: "Multi-dimension", d: "accompagner dans toutes ses couches." },
            ].map((c) => (
              <div key={c.t} className="glass card-hover rounded-2xl p-6">
                <c.icon className="h-6 w-6 text-gold" />
                <h3 className="font-display text-xl text-white mt-4">{c.t}</h3>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* The great shift */}
        <div className="mt-24 text-center reveal">
          <span className="kicker">Le grand shift</span>
          <h2 className="font-display text-3xl md:text-5xl font-semibold text-white mt-3">
            Raisonnement{" "}
            <span className="text-white/40">→</span>{" "}
            <span className="aqua-gradient-text">Résonance</span>
          </h2>
          <p className="mt-4 text-white/60 max-w-2xl mx-auto">
            Nous sommes littéralement des « bouteilles d'eau » géantes. Quand ton eau
            intérieure est alignée, le champ change — et l'intégration devient simple.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          <div className="reveal glass rounded-3xl p-8 border-white/10">
            <div className="text-sm uppercase tracking-widest text-white/45">{shift.left.subtitle}</div>
            <h3 className="font-display text-2xl text-white/80 mt-2">{shift.left.title}</h3>
            <ul className="mt-5 space-y-2.5">
              {shift.left.items.map((i) => (
                <li key={i} className="text-white/55 pl-4 border-l border-white/10">{i}</li>
              ))}
            </ul>
          </div>
          <div className="reveal glass-strong rounded-3xl p-8">
            <div className="text-sm uppercase tracking-widest text-aqua">{shift.right.subtitle}</div>
            <h3 className="font-display text-2xl gold-gradient-text mt-2">{shift.right.title}</h3>
            <ul className="mt-5 space-y-2.5">
              {shift.right.items.map((i) => (
                <li key={i} className="text-white/90 pl-4 border-l border-[#e8c874]/40">{i}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Multi-conscience + quantum */}
        <div className="mt-24 grid lg:grid-cols-2 gap-8">
          <div className="reveal glass rounded-3xl p-8 md:p-10">
            <span className="kicker">Multi-conscience</span>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-3">
              Ce que 97% des praticiens n'activent pas
            </h3>
            <p className="mt-4 text-white/65 leading-relaxed">
              Sentir simultanément plusieurs niveaux — corps, émotion, symbolique,
              énergie, intention — rester connecté(e) sans te mélanger, percevoir
              plus finement sans projection.
            </p>
            <p className="mt-4 text-gold font-display text-xl">
              Tu ne deviens pas plus perché(e). Tu deviens plus précis(e).
            </p>
          </div>
          <div className="reveal glass rounded-3xl p-8 md:p-10">
            <span className="kicker">Le paradigme quantique</span>
            <h3 className="font-display text-2xl md:text-3xl text-white mt-3">
              La réalité n'est pas figée : elle est faite de potentiels
            </h3>
            <ul className="mt-4 space-y-3">
              {[
                "ton état sélectionne ce qui devient accessible",
                "quand tu te règles, tu règles l'espace",
                "tu calibres, tu stabilises, tu transmets",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3 text-white/75">
                  <Atom className="h-4 w-4 text-aqua mt-1 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <a href={RESERVE_URL} target="_blank" rel="noreferrer" className="btn-ghost px-6 py-3 inline-block text-sm">
                J'entre dans la résonance
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
